import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Cable } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../Common/LoadingSpinner';
import toast from 'react-hot-toast';

const LoginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string().required('Password is required'),
});

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);
    try {
      await login(values);
      toast.success('Login successful!');
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.message || 'Login failed. Please try again.';
      toast.error(message);
      setErrors({ password: message });
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen flex flex-col md:flex-row">

      {/* Left Side - Image */}
      <div className="hidden md:flex w-full md:w-4/6 h-full">
        <img
          src="/SGPNC_Logo_FINAL_page-0001.jpg"
          alt="SGPNC Logo"
          className="object-cover w-full h-full"
        />
      </div>

      {/* Right Side - Form and Footer */}
      <div className="flex w-full md:w-1/2 flex-col items-center justify-center bg-white px-6 lg:px-12 overflow-y-auto pt-24">

        {/* Bordered Form */}
        <div className="max-w-md w-full space-y-4 py-6 border border-gray-300 rounded-xl shadow-sm p-6 bg-white">
          <div>
            <div className="mx-auto h-10 w-10 bg-primary-600 rounded-full flex items-center justify-center">
              <Lock className="h-5 w-5 text-white" />
            </div>
            <h2 className="mt-2 text-center text-2xl font-bold text-gray-900">
              Sign in to your account
            </h2>
            <p className="mt-1 text-center text-sm text-gray-600">
              Enter your credentials to access the compliance system
            </p>
          </div>

          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={LoginSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form className="space-y-4">
                {touched.password && errors.password && !touched.email && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative text-sm" role="alert">
                    <span className="block sm:inline">{errors.password}</span>
                  </div>
                )}

                <div className="space-y-3">
                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email address
                    </label>
                    <div className="mt-1 relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Field
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder="Enter your email"
                        className={`appearance-none relative block w-full px-3 py-2 pl-10 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${errors.email && touched.email ? 'border-red-300' : 'border-gray-300'
                          }`}
                      />
                    </div>
                    <ErrorMessage name="email" component="p" className="mt-1 text-sm text-red-600" />
                  </div>

                  {/* Password Field */}
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <div className="mt-1 relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Field
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder="Enter your password"
                        className={`appearance-none relative block w-full px-3 py-2 pl-10 pr-10 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${errors.password && touched.password ? 'border-red-300' : 'border-gray-300'
                          }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <ErrorMessage name="password" component="p" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <Link to="/forgot-password" className="font-medium text-primary-600 hover:text-primary-500">
                    Forgot your password?
                  </Link>
                </div>

                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? <LoadingSpinner size="sm" text="Signing in..." /> : 'Sign in'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>

        {/* Powered By - Outside the Form Box */}
        <div className="text-center text-sm text-gray-600 flex items-center justify-center mt-1">
          <Cable className="h-6 w-6 mr-2" />
          <span className='text-base text-lg mr-1'>Powered By</span>
          <a href="https://www.nexauradigital.com/" target="_blank" rel="noopener noreferrer">
            <img
              className='h-36 w-36'
              src="https://res.cloudinary.com/dps6rupsi/image/upload/v1750170767/contact-form-files/1750170767149-Nexauralogo.svg.svg"
              alt="Nexaura Digital"
            />
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
